import { NextIncomingMessage } from 'next/dist/server/request-meta';

import { match as matchLocale } from '@formatjs/intl-localematcher';
import type { IncomingHttpHeaders } from 'http';
import Negotiator from 'negotiator';
import type Rollbar from 'rollbar';
import Cookies from 'universal-cookie';

declare global {
  interface Window {
    // Browser Rollbar is injected by a script tag when present, so it only
    // exists as an optional window global.
    Rollbar?: Rollbar;
  }
}

const i18n = {
  defaultLocale: 'es',
  locales: ['en', 'es'],
} as const;

export type Locale = (typeof i18n)['locales'][number];

export default function getLocale(
  req: NextIncomingMessage & {
    cookies: Partial<Record<string, string>>;
  }
) {
  // Use negotiator and intl-localematcher to get best locale
  const headerLanguages: string[] = new Negotiator({
    // Next 16 widened NextIncomingMessage.headers to IncomingHttpHeaders | Headers;
    // in the pages router this is always the Node IncomingHttpHeaders at runtime.
    headers: req.headers as IncomingHttpHeaders,
  }).languages();

  const cookies = new Cookies(req.cookies);
  const langCookie = cookies.get<string | undefined>('onark-lang');

  // if there is a language, set it to the top of the list
  if (langCookie) headerLanguages.unshift(langCookie);

  const availableLocales: string[] = [...i18n.locales];

  let locale: Locale = i18n.defaultLocale;

  try {
    locale = matchLocale(
      headerLanguages,
      availableLocales,
      i18n.defaultLocale
    ) as (typeof i18n.locales)[number];
  } catch (e) {
    if (typeof window !== 'undefined') {
      // `?.` also fixes a latent crash: the old `window.Rollbar.error` threw
      // if the Rollbar script hadn't loaded.
      window.Rollbar?.error(
        e instanceof Error ? e : String(e),
        headerLanguages,
        availableLocales
      );
    }
  }

  return locale;
}
